import { Injectable, forwardRef, Inject } from '@angular/core';
import { VcmApiHandlerService } from '../api-handler/api-handler.service';
import { environment } from '../../../../environments/environment';
import { ApiResponseDispatcher } from '../../../store/api-response/api-response-dispatcher';
import { CommonDispatcher } from '../../../store/common/common-dispatcher';
import { MatDialog } from '@angular/material';
import { Router } from '@angular/router';
import { DialogBoxComponent } from '../../../common/components/dialog-box/dialog-box.component';
import { CustomerDispatcher } from '../../../store/customer/customer-dispatcher';
import { SpinnerService } from '../../../common/components/spinner/overlay-spinner.service';
import { SpinnerRef } from '../../../common/components/spinner/overlay-ref.service';
import { SnackBarService } from '../../../common/services/snack-bar/snack-bar.service';
import { TranslationService } from '../../../common/services/languages/translation.service';
import { AppState } from '../../../store/state.model';
import { NgRedux } from '@angular-redux/store';

@Injectable()
export class OrchestrationService {
  constructor(
    @Inject(forwardRef(() => VcmApiHandlerService)) public apiHandler: VcmApiHandlerService,
    public apiDispatcher: ApiResponseDispatcher,
    public commonDispatcher: CommonDispatcher,
    public customerDispatcher: CustomerDispatcher,
    public dialog: MatDialog,
    public router: Router,
    public spinner: SpinnerService,
    private snackBarService: SnackBarService,
    private translate: TranslationService,
    public ngRedux: NgRedux<AppState>,
  ) { }
  /**
  * @function setResponse set the api response error or success
  * @arg method: string
  * @arg type: string
  * @arg body: object
  * @arg spinnerRef: SpinnerRef
  * @returns void
  **/
  private setResponse(method: string, type: string, body: object, spinnerRef: SpinnerRef): void {
    spinnerRef.close();
    this.apiDispatcher[method](Object.assign(body, { type }));
    if (type === 'error') {
      this.showError(body);
    }
  }
  /**
   * @function getVcmHistory to get vcm vin history
   * @arg vin of string type (optional)
   * @arg chipId of string type (optional)
   * @arg suppressError if true, won't show error dialog (for fallback scenarios)
   * @returns void
   **/
  public getVcmHistory(vin?: string, chipId?: string, suppressError?: boolean): void {
    const spinnerRef = this.spinner.open();
    this.apiDispatcher.setVcmHistory({});
    const params: any = {};
    if (vin) {
      params.vin = vin;
    }
    if (chipId) {
      params.chipId = chipId;
    }
    this.apiHandler.getRequest(environment.vcm_admin.apis.vcm_history_vin, {
      type: 'get_vcm_history', params
    }).subscribe({
      next: (response) => this.setResponse('setVcmHistory', 'success', response, spinnerRef),
      error: (error) => {
        spinnerRef.close();
        this.apiDispatcher.setVcmHistory(Object.assign(error, { type: 'error' }));
        if (!suppressError) {
          this.showError(error);
        }
      }
    });
  }

  /**
   * @function clearVin to clear the vin in vcm records]
   * @arg vin of string type
   * @returns void
   **/
  public clearVin(vin: string) {
    const spinnerRef = this.spinner.open();
    this.apiDispatcher.setClearVin({});
    let isCleared = false;
    this.apiHandler.postRequest(environment.vcm_admin.apis.vcm_clear_vin, {}, {
      type: 'clear_vin', params: { vin }
    }).subscribe({
      next: (response) => this.setResponse('setClearVin', 'success', {}, spinnerRef),
      error: (error) => this.setResponse('setClearVin', 'error', error, spinnerRef)
    })
    spinnerRef.close();
  }

  /**
   * @function clearChipId to clear the chip ID for 24MM vehicles
   * @arg chipId of string type
   * @arg vin of string type
   * @returns void
   **/
  public clearChipId(chipId: string, vin: string) {
    const spinnerRef = this.spinner.open();
    this.apiDispatcher.setClearChipId({});
    const chipIdLength = chipId ? chipId.length : 10;
    const zerosChipId = '0'.repeat(chipIdLength);
    const body = {
      'chip-id': zerosChipId
    };
    this.apiHandler.putRequest(environment.vcm_admin.apis.details, body, {
      type: 'clear_chip_id', params: { chipId, vin }
    }).subscribe({
      next: (response) => this.setResponse('setClearChipId', 'success', {}, spinnerRef),
      error: (error) => this.setResponse('setClearChipId', 'error', error, spinnerRef)
    })
  }

  /**
| * @function findVehicle action to find vehicle (api call)
| * @arg vin of string type (optional)
| * @arg chipId of string type (optional)
| * @returns void
| **/
  public findVehicle(vin?: string, chipId?: string): void {
    const spinnerRef = this.spinner.open();
    this.apiDispatcher.setfindVehicleResponse({});
    const params: any = {};
    if (vin) {
      params.vin = vin;
    }
    if (chipId) {
      params.chipId = chipId;
    }
    this.apiHandler.getRequest(environment.vcm_admin.apis.details, {
      type: 'cvs_details', params
    }).subscribe({
      next: (response) => this.setResponse('setfindVehicleResponse', 'success', response, spinnerRef),
      error: (error) => this.setResponse('setfindVehicleResponse', 'error', error, spinnerRef)
    });
  }
  /**
    * @function routeError route to error on unauthorized
    * @returns void
    **/
  private routeError(): void {
    this.router.navigate(['dashboard/error'], { queryParams: {}, skipLocationChange: false });
  }
  /**
   * @method showError displays the error message
   * @arg error error response from the api
   */
  public showError(error: object): void {
    if (error['errorCode'] === 'UNAUTHORIZED') {
      this.routeError();
    } else {
      this.commonDispatcher.setErrorCode(error['errorCode']);
      this.dialog.open(DialogBoxComponent, {
        data: {
          type: 'api-error',
          message: error['errorMessage'],
          endPoint: error['api']
        },
        width: '30em',
        height: 'auto'
      });
    }
  }
}
